import storage from '@/services/storage'
import EventBus from '@/eventBus'
import store from '@/vuex/store'
import router from '@/router'

/**
 * Safe localStorage read for this service.
 * storage.ts's getValue calls JSON.parse with no error handling. If a checklist
 * key is corrupted (truncated write, browser extension, storage-limit flush) it
 * would throw on the editor's mount path and break the editor for every reload
 * until the user manually clears storage. We guard against that here rather than
 * patching the shared storage.ts (which is used by unrelated modules).
 */
const safeGetValue = (key, fallback) => {
  try {
    return storage.getValue(key, fallback)
  } catch (_) {
    // Corrupted entry — remove it and return the fallback so the editor stays healthy.
    try { storage.remove(key) } catch (__) { /* ignore if remove also fails */ }
    return fallback
  }
}

export const ONBOARDING_IKEA_CHECKLIST_SOURCE = 'onboarding-ikea-checklist'
export const ONBOARDING_IKEA_CHECKLIST_EVENT = 'onboarding-ikea-checklist:updated'

/**
 * sessionStorage key for the debug override.
 * Set once when ?onboardingIkeaChecklistDebug=1 is seen in the URL;
 * survives client-side redirects that strip query params for the duration of the session.
 * This is intentionally session-scoped (not localStorage) so it never bleeds into
 * production for real users across sessions. The Unleash variant is still the
 * production gate; this key is only a QA/reviewer escape hatch.
 */
export const DEBUG_FLAG_SESSION_KEY = 'onboardingIkeaChecklistDebug'

// In-memory flag: set when the user clicks Close ("X") in the current page session.
// Resets on page reload (intentional — the debug param re-shows after a reload, which
// is the desired QA behaviour). This is NOT persisted; dismissForever() handles persistence.
// isVisible() checks this BEFORE the debug-force-show so an explicit in-session Close
// is always honoured, even when ?onboardingIkeaChecklistDebug=1 is active.
let closedThisSession = false

export const markClosedThisSession = () => {
  closedThisSession = true
}

export const isClosedThisSession = () => closedThisSession

/**
 * Called once at app boot (main.ts) before any router redirects run.
 * If the raw URL contains ?onboardingIkeaChecklistDebug=1, persist it in
 * sessionStorage so that isChecklistEnabled() stays true even after the
 * router strips the param during its internal redirects.
 */
export const captureDebugParamIfPresent = () => {
  try {
    const value = new URLSearchParams(window.location.search).get('onboardingIkeaChecklistDebug')
    if (value === '1') {
      sessionStorage.setItem(DEBUG_FLAG_SESSION_KEY, '1')
    }
  } catch (_) {
    // sessionStorage unavailable — silently ignore
  }
}

export const STEPS = {
  PREVIEW: 'preview',
  PRESENT: 'present',
}

const ALL_STEPS = [STEPS.PREVIEW, STEPS.PRESENT]

const storageKey = (presentationId) => `onboarding-ikea-checklist:${presentationId}`

// Global (non-presentation-specific) key: set when the user clicks "X" to permanently
// hide the checklist across ALL presentations. Once set, the checklist never appears again.
export const DISMISSED_FOREVER_KEY = 'onboarding-ikea-checklist:dismissed-forever'

// Global (account/user-scoped, NOT keyed by presentationId) record of which checklist
// steps the user has EVER completed. localStorage is per-browser-profile = per-logged-in
// user in practice, so a step finished in one presentation is remembered for every other
// presentation the same user opens. This is the source of truth for cross-presentation
// progress: per-presentation state (storageKey(presentationId)) is the local working copy,
// but a step is "Done" globally the moment it is completed in ANY presentation.
// Shape: { [stepKey]: true }
export const GLOBAL_COMPLETED_STEPS_KEY = 'onboarding-ikea-checklist:completed-steps'

// Global (account/user-scoped) flag: set the first time the user completes the final
// (3rd) step and the celebration confetti is shown. Because step progress is now global
// (GLOBAL_COMPLETED_STEPS_KEY), a freshly-opened presentation can already be 3/3 on mount;
// without this flag the per-presentation `celebrated` flag would be false on that new state
// and the confetti would re-fire every time a completed user opens/creates a presentation.
// This flag makes the confetti fire EXACTLY ONCE, globally, ever.
export const GLOBAL_CONFETTI_SHOWN_KEY = 'onboarding-ikea-checklist:confetti-shown'

const isConfettiShownGlobally = () => !!safeGetValue(GLOBAL_CONFETTI_SHOWN_KEY, false)

const markConfettiShownGlobally = () => {
  storage.setValue(GLOBAL_CONFETTI_SHOWN_KEY, true)
}

const getGlobalCompletedSteps = () => safeGetValue(GLOBAL_COMPLETED_STEPS_KEY, {}) || {}

const markStepDoneGlobally = (step) => {
  if (!ALL_STEPS.includes(step)) return
  const completed = getGlobalCompletedSteps()
  if (completed[step]) return
  storage.setValue(GLOBAL_COMPLETED_STEPS_KEY, { ...completed, [step]: true })
}

// Merge the global completed-steps record into a per-presentation state's steps so that
// any step the user finished in a different presentation already shows as Done. Global
// progress only ever ADDS completions (true wins) — it never un-completes a local step.
const mergeGlobalCompletedSteps = (state) => {
  if (!state) return state
  const completed = getGlobalCompletedSteps()
  const steps = { ...state.steps }
  let changed = false
  ALL_STEPS.forEach((step) => {
    if (completed[step] && !steps[step]) {
      steps[step] = true
      changed = true
    }
  })
  return changed ? { ...state, steps } : state
}

const isDismissedForever = () => !!safeGetValue(DISMISSED_FOREVER_KEY, false)

const dismissForever = () => {
  storage.setValue(DISMISSED_FOREVER_KEY, true)
  EventBus.$emit(ONBOARDING_IKEA_CHECKLIST_EVENT, null)
}

const defaultState = () => ({
  enrolled: true,
  dismissed: false,
  celebrated: false,
  steps: {
    [STEPS.PREVIEW]: false,
    [STEPS.PRESENT]: false,
  },
})

const getState = (presentationId) => {
  if (!presentationId) return null
  const state = safeGetValue(storageKey(presentationId), null)
  if (!state) return null
  // Reflect cross-presentation progress on read: a step completed in any OTHER
  // presentation shows as Done here. Pure merge (no write-back) — global progress only
  // ever adds completions, so returning the merged view is sufficient and keeps getState
  // side-effect-free. enroll() seeds new states with global progress at write time.
  return mergeGlobalCompletedSteps(state)
}

const notify = (presentationId) => {
  EventBus.$emit(ONBOARDING_IKEA_CHECKLIST_EVENT, presentationId)
}

const enroll = (presentationId) => {
  if (!presentationId) return null
  const existing = getState(presentationId)
  if (existing) return existing
  // Seed a brand-new presentation's state with the user's global completed-steps so
  // that opening a new presentation immediately shows steps already finished elsewhere
  // as Done (the core cross-presentation requirement).
  const state = mergeGlobalCompletedSteps(defaultState())
  storage.setValue(storageKey(presentationId), state)
  notify(presentationId)
  return state
}

const markStepDone = (presentationId, step) => {
  const state = getState(presentationId)
  if (!state || !ALL_STEPS.includes(step) || state.steps[step]) return state
  const next = {
    ...state,
    steps: { ...state.steps, [step]: true },
  }
  storage.setValue(storageKey(presentationId), next)
  // Record the completion at user/account scope so every OTHER presentation marks this
  // step Done too. This is what makes progress persist across presentations.
  markStepDoneGlobally(step)
  notify(presentationId)
  return next
}

const markCelebrated = (presentationId) => {
  const state = getState(presentationId)
  if (!state || state.celebrated) return state
  const next = { ...state, celebrated: true }
  storage.setValue(storageKey(presentationId), next)
  notify(presentationId)
  return next
}

const dismiss = (presentationId) => {
  const state = getState(presentationId)
  if (!state) return null
  const next = { ...state, dismissed: true }
  storage.setValue(storageKey(presentationId), next)
  notify(presentationId)
  return next
}

const clear = (presentationId) => {
  if (!presentationId) return
  storage.remove(storageKey(presentationId))
  notify(presentationId)
}

const isAllDone = (state) => {
  if (!state) return false
  return ALL_STEPS.every((s) => state.steps[s])
}

/**
 * Returns true when the onboarding checklist should be active for this page load.
 * Three paths, checked in order:
 *   1. Normal: Unleash variant flag 'AHA-42696-onboarding-ikea-checklist-presenter' is on.
 *   2. Debug/sandbox (live param): ?onboardingIkeaChecklistDebug=1 still in the URL.
 *   3. Debug/sandbox (persisted): the param was seen earlier this session before the
 *      Vue router redirected and stripped it. captureDebugParamIfPresent() sets this
 *      in sessionStorage at app boot so the flag survives client-side navigation.
 */
/**
 * True when the debug/QA escape hatch is active for this session — either the
 * ?onboardingIkeaChecklistDebug=1 param is live in the URL, or it was captured
 * into sessionStorage at boot before the router stripped it.
 *
 * This is deliberately SEPARATE from isChecklistEnabled(): the debug flag must
 * be a reliable FORCE-ON for testing, which means it has to override even the
 * "dismissed forever" guard. A reviewer who clicked "X" on a previous sandbox
 * must still be able to see the checklist again just by re-appending the param —
 * otherwise localStorage from a prior round silently suppresses it forever and
 * the tester (correctly) reports "still nothing". Production users never hit
 * this path because the param/sessionStorage flag is never set for them.
 */
export const isDebugForced = () => {
  try {
    if (new URLSearchParams(window.location.search).get('onboardingIkeaChecklistDebug') === '1') {
      return true
    }
    return sessionStorage.getItem(DEBUG_FLAG_SESSION_KEY) === '1'
  } catch (_) {
    return false
  }
}

export const isChecklistEnabled = () => {
  const canUse = store.getters['featureToggles/canUseOnboardingIkeaChecklist']
  if (canUse) return true
  return isDebugForced()
}

/**
 * Called when the presentation editor mounts (renderPresentationDetail becomes true).
 * Enrolls the presentation in the ikea checklist for ALL presentations (unless
 * the user has permanently dismissed the checklist by clicking "X").
 * Also strips the legacy deepLinkSource / onboardingChecklistDebug params from the URL
 * when present — they are no longer required for enrollment but may still arrive via
 * deep-links and should be cleaned up.
 */
const maybeEnrollFromRoute = (presentationId) => {
  if (!isChecklistEnabled()) return
  if (!presentationId) return
  // Skip enrollment if the user has permanently dismissed the checklist —
  // UNLESS the debug param is forcing it on. In that case we still enroll
  // so the component can load its state, but we do NOT clear the dismissed-
  // forever flag: a user's explicit X-click must be truly permanent and must
  // survive debug-param sessions. The isVisible() guard in the component
  // already handles force-show via `isDismissedForever() && !isDebugForced()`,
  // so visibility is correctly overridden without touching the flag.
  if (isDismissedForever()) {
    if (!isDebugForced()) return
    // Debug is on: enroll so state loads, but keep dismissed-forever intact.
  }
  enroll(presentationId)
  // Clean up legacy / debug URL params so they don't persist across refreshes.
  const route = router.currentRoute
  const hasLegacyParams = route.query.deepLinkSource || route.query.onboardingChecklistDebug
  if (hasLegacyParams) {
    const query = { ...route.query }
    delete query.deepLinkSource
    delete query.onboardingChecklistDebug
    router.replace({ query }).catch(() => {})
  }
}

export default {
  STEPS,
  getState,
  enroll,
  markStepDone,
  markCelebrated,
  dismiss,
  clear,
  isAllDone,
  isDismissedForever,
  dismissForever,
  isChecklistEnabled,
  isDebugForced,
  isClosedThisSession,
  markClosedThisSession,
  maybeEnrollFromRoute,
  captureDebugParamIfPresent,
  getGlobalCompletedSteps,
  markStepDoneGlobally,
  isConfettiShownGlobally,
  markConfettiShownGlobally,
}
